import PropTypes from "prop-types";
import "./PassengersFieldset.scss";
import NumberInputFormik from "app/pages/.shared/NumberInputFormik";
import { FormattedMessage } from "react-intl";
import AlertMessage from "app/components/ui/AlertMessage/AlertMessage";
import { MAX_PARTICIPANTS, AGE_RANGE } from "app/constants";
import { FieldArray, useFormikContext } from "formik";
import DateSelectorInputFormik from "app/pages/.shared/DateSelector/DateSelectorInputFormik";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import { useCallback } from "react";
import { toPassengerCount } from "app/utils/utils";

const checkMinCount = value => {
	return value > 0;
};

const PassengersFieldset = ({
	adultOnly,
	isChildrenAgeRequired,
	onAdultsSelect = () => {},
	onChildrenSelect = () => {},
	onInfantsSelect = () => {},
	onChildrenAgeChange = () => {},
	onInfantsAgeChange = () => {},
}) => {
	const { values, errors = {}, isSubmitting, submitCount } = useFormikContext();

	const adultCount = toPassengerCount(values?.adults);
	const infantCount = toPassengerCount(values?.infants);
	const childCount = toPassengerCount(values?.children);

	const occupanciesCount = adultCount + infantCount + childCount;
	const checkMaxParticipants = useCallback(
		() => occupanciesCount < MAX_PARTICIPANTS,
		[occupanciesCount]
	);

	return (
		<div className="passengers-fieldset">
			{adultOnly && (
				<AlertMessage
					message={<FormattedMessage id="search.engine.adult.only.short.warning" />}
				/>
			)}

			<div className="passengers-fieldset__item">
				<div className="passengers-fieldset__label">
					<label htmlFor="adults">
						<FormattedMessage id="search.engine.adults.label" tagName="span" />
						{isChildrenAgeRequired && (
							<FormattedMessage
								id="search.engine.adults.age.range.label"
								tagName="span"
							/>
						)}
					</label>
				</div>
				<div className="passengers-fieldset__input">
					<NumberInputFormik
						id="adults"
						name="adults"
						onChange={onAdultsSelect}
						canIncrement={checkMaxParticipants}
					/>
				</div>
			</div>

			<FieldArray name="childrenBirthdates">
				{({ remove, push }) => {
					const onChildrenCountChange = value => {
						if (value > childCount) {
							push({
								day: undefined,
								month: undefined,
								year: undefined,
							});
						} else {
							remove(Number(value));
						}
						onChildrenSelect(Number(value));
					};

					return (
						<>
							<div className="passengers-fieldset__item">
								<div className="passengers-fieldset__label">
									<label htmlFor="children">
										<FormattedMessage
											id="search.engine.children.label"
											tagName="span"
										/>
										<FormattedMessage
											id="search.engine.children.age.range.label"
											tagName="span"
											values={{
												minAge: 2,
												maxAge: isChildrenAgeRequired ? 17 : 11,
											}}
										/>
									</label>
								</div>
								<div className="passengers-fieldset__input">
									<NumberInputFormik
										id="children"
										name="children"
										canDecrement={value => checkMinCount(value)}
										canIncrement={checkMaxParticipants}
										onChange={
											isChildrenAgeRequired
												? onChildrenCountChange
												: onChildrenSelect
										}
									/>
								</div>
							</div>

							<FormErrorMessages
								errors={errors}
								isSubmitting={isSubmitting}
								submitCount={submitCount}
								fieldsFilter={["childrenBirthdates"]}
							/>

							{isChildrenAgeRequired &&
								values.childrenBirthdates.map((childrenBirthdate, index) => {
									return (
										<div
											key={index}
											className="passengers-fieldset__item passengers-fieldset__item--birthdate"
										>
											<div className="passengers-fieldset__label">
												<label htmlFor={`children-age-${index + 1}`}>
													<FormattedMessage
														values={{ index: index + 1 }}
														id="payment.passengers.child"
														tagName="span"
													/>
												</label>
											</div>
											<div className="passengers-fieldset__input">
												<DateSelectorInputFormik
													formatMonth="short"
													isChildrenAgeRequired={true}
													label={
														<FormattedMessage id="search.engine.birthdate.label" />
													}
													id={`children-age-${index + 1}`}
													name={`childrenBirthdates.${index}`}
													data-testid={`childrenBirthdates-${index}-input`}
													onUpdate={value => {
														onChildrenAgeChange(value, index);
													}}
													rangeYear={AGE_RANGE.FROM_2_TO_17}
													isSubmitting={isSubmitting}
												/>
											</div>
										</div>
									);
								})}
						</>
					);
				}}
			</FieldArray>

			<FieldArray name="infantsBirthdates">
				{({ remove, push }) => {
					const onInfantCountChange = value => {
						if (value > infantCount) {
							push({
								day: undefined,
								month: undefined,
								year: undefined,
							});
						} else {
							remove(Number(value));
						}
						onInfantsSelect(Number(value));
					};

					return (
						<>
							<div className="passengers-fieldset__item">
								<div className="passengers-fieldset__label">
									<label htmlFor="infants">
										<FormattedMessage
											id="search.engine.infants.label"
											tagName="span"
										/>
										<FormattedMessage
											id="search.engine.infants.age.range.label"
											tagName="span"
										/>
									</label>
								</div>
								<div className="passengers-fieldset__input">
									<NumberInputFormik
										id="infants"
										name="infants"
										canDecrement={value => checkMinCount(value)}
										canIncrement={checkMaxParticipants}
										onChange={
											isChildrenAgeRequired
												? onInfantCountChange
												: onInfantsSelect
										}
									/>
								</div>
							</div>

							<FormErrorMessages
								errors={errors}
								submitCount={submitCount}
								fieldsFilter={["infantsBirthdates"]}
								isSubmitting={isSubmitting}
							/>

							{isChildrenAgeRequired &&
								values.infantsBirthdates.map((infantBirthdate, index) => {
									return (
										<div
											key={index}
											className="passengers-fieldset__item passengers-fieldset__item--birthdate"
										>
											<div className="passengers-fieldset__label">
												<label htmlFor={`infant-age-${index + 1}`}>
													<FormattedMessage
														values={{ index: index + 1 }}
														tagName="span"
														id="payment.passengers.infant"
													/>
												</label>
											</div>
											<div className="passengers-fieldset__input">
												<DateSelectorInputFormik
													formatMonth="short"
													isChildrenAgeRequired={isChildrenAgeRequired}
													label={
														<FormattedMessage id="search.engine.birthdate.label" />
													}
													id={`infant-age-${index + 1}`}
													name={`infantsBirthdates.${index}`}
													data-testid={`infantsBirthdates-${index}-input`}
													onUpdate={value => {
														onInfantsAgeChange(value, index);
													}}
													rangeYear={AGE_RANGE.FROM_0_TO_2}
													isSubmitting={isSubmitting}
												/>
											</div>
										</div>
									);
								})}
						</>
					);
				}}
			</FieldArray>
		</div>
	);
};

PassengersFieldset.propTypes = {
	adultOnly: PropTypes.bool,
	isChildrenAgeRequired: PropTypes.bool,
	onAdultsSelect: PropTypes.func,
	onChildrenSelect: PropTypes.func,
	onInfantsSelect: PropTypes.func,
	onChildrenAgeChange: PropTypes.func,
	onInfantsAgeChange: PropTypes.func,
};

export default PassengersFieldset;
